import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignDto, CampaignManagerDto } from '@/app/common/general-types/campaigns.dto';
import { UgcProductResponseDto } from '@/app/common/general-types/products.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { CreateCampaignComponent } from '@/app/molecules/create-campaign/create-campaign.component';
import { BrandProductsCreateComponent } from '@/app/pages/my-brand/molecules/brand-products-create/brand-products-create.component';
import { BusinessAccountService } from '@/app/services/business-account/business-account.service';
import { CampaignService } from '@/app/services/campaign/campaign.service';
import { ImageService } from '@/app/services/image/image.service';
import { ProductService } from '@/app/services/product/product.service';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import { sectionStatus } from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';

/** Basic Info: title, product (with image and description), campaign group, who posted it. */
@Component({
  selector: 'mbt-briefing-basic',
  imports: [BriefingSection, BriefingDrawer, MbaIcon, DatePipe, CreateCampaignComponent, BrandProductsCreateComponent],
  template: `
    @if (svc.brief(); as brief) {
    <mbt-briefing-section key="basic" icon="package" title="Basic Info" [flash]="flash()"
      helper="The product creators will feature, and where this brief sits in your account."
      [status]="status()" (edit)="open()">
      @if (status() === 'missing') {
        <div [class]="bf.empty">
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <b [class]="bf.emptyTitle">Add a title and a product</b>
            <span [class]="bf.sub">Creators see the product before anything else.</span>
          </span>
          <button type="button" [class]="bf.btnPrimary" (click)="open()"><mba-icon name="plus" size="size-4" />Set up</button>
        </div>
      }
      <div class="grid grid-cols-[132px_1fr] gap-5 max-[640px]:grid-cols-1">
        @if (product()?.productImage) {
          <img class="aspect-square w-[132px] rounded-lg border border-(--border-subtle) object-cover"
            [src]="images.getImageSource(product()!.productImage, 'gallery_xl')" [alt]="product()!.productName" />
        } @else {
          <span class="grid aspect-square w-[132px] place-items-center rounded-lg bg-(--bg-alt) text-(--fg-muted)"><mba-icon name="image" size="size-6" /></span>
        }
        <dl class="m-0 grid grid-cols-[140px_1fr] max-[640px]:grid-cols-1">
          <dt [class]="bf.k + ' border-b border-(--border-subtle) py-2.5'">Brief title</dt>
          <dd class="m-0 border-b border-(--border-subtle) py-2.5"><b [class]="bf.display + ' text-[19px]'">{{ brief.title }}</b>
            @if (brief.brief_goal_name) { <span [class]="bf.tag + ' ml-2 align-middle'">{{ brief.brief_goal_name }}</span> }
          </dd>
          <dt [class]="bf.k + ' border-b border-(--border-subtle) py-2.5'">Product</dt>
          <dd class="m-0 flex flex-col gap-1 border-b border-(--border-subtle) py-2.5">
            <b [class]="bf.display + ' text-[19px]'">{{ product()?.productName || 'No product' }}</b>
            @if (product()?.productDescription) { <span class="text-[13px] text-(--fg-2)">{{ product()!.productDescription }}</span> }
          </dd>
          <dt [class]="bf.k + ' border-b border-(--border-subtle) py-2.5'">Campaign group</dt>
          <dd class="m-0 border-b border-(--border-subtle) py-2.5">
            @if (campaign()?.campaignName) {
              <span class="inline-flex items-center gap-1.5 text-sm font-bold text-(--app-accent-strong)"><mba-icon name="folder" size="size-4" />{{ campaign()!.campaignName }}</span>
            } @else {
              <span class="text-sm text-(--fg-muted)">No campaign group</span>
            }
          </dd>
          <dt [class]="bf.k + ' py-2.5'">Posted</dt>
          <dd class="m-0 flex items-center gap-2 py-2.5 text-sm">
            @if (postedBy(); as user) {
              <span [class]="bf.avatar + ' size-7 text-xs'">
                @if (user.userAccount.photoURL) { <img class="size-full rounded-full object-cover" [src]="images.getImageSource(user.userAccount.photoURL, 'profile_xl')" alt="" /> }
                @else { {{ user.userAccount.firstName.charAt(0) }} }
              </span>
              <b>{{ user.userAccount.firstName }} {{ user.userAccount.lastName.charAt(0) }}.</b>
            }
            <span class="text-(--fg-3)">{{ postedBy() ? '· ' : '' }}{{ brief.createdDate | date: 'd MMM y' }}</span>
          </dd>
        </dl>
      </div>
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="package" title="Basic Info" [mode]="status() === 'missing' ? 'Set up' : 'Edit'"
      helper="The product creators will feature, and where this brief sits in your account."
      [saving]="saving()" (closed)="editing.set(false)" (save)="save()">
      <label [class]="bf.field">
        <span><b [class]="bf.fieldLabel">Brief title</b> <span [class]="bf.fieldSub">· Only you see this</span></span>
        <input type="text" [class]="bf.input" maxlength="200" [value]="title()" (input)="title.set(value($event))" />
      </label>
      <div [class]="bf.field">
        <span class="flex items-center gap-2">
          <label for="bf-product" [class]="bf.fieldLabel">Product</label>
          <span [class]="bf.fieldSub">What creators will receive and feature</span>
          <span class="flex-1"></span>
          <button type="button" [class]="bf.addLink" (click)="addingProduct.set(true)"><mba-icon name="plus" size="size-4" />Add product</button>
        </span>
        <select id="bf-product" [class]="bf.input" (change)="productId.set(value($event))">
          <option value="" disabled [selected]="!productId()">Select a product</option>
          @for (product of products(); track product.id) {
            <option [value]="product.id" [selected]="product.id === productId()">{{ product.productName }}</option>
          }
        </select>
        @if (selectedProduct()?.productDescription) {
          <div [class]="bf.info"><mba-icon name="package" size="size-4" /><span>{{ selectedProduct()!.productDescription }}</span></div>
        }
      </div>
      <div [class]="bf.field">
        <span class="flex items-center gap-2">
          <label for="bf-campaign" [class]="bf.fieldLabel">Campaign group</label>
          <span class="flex-1"></span>
          <button type="button" [class]="bf.addLink" (click)="addingCampaign.set(true)"><mba-icon name="plus" size="size-4" />Add campaign</button>
        </span>
        <span [class]="bf.fieldSub">Groups keep related briefs together in your account</span>
        <select id="bf-campaign" [class]="bf.input" (change)="campaignId.set(value($event))">
          <option value="" [selected]="!campaignId()">No campaign group</option>
          @for (campaign of campaigns(); track campaign.id) {
            <option [value]="campaign.id" [selected]="campaign.id === campaignId()">{{ campaign.campaignName }}</option>
          }
        </select>
      </div>
      <div [class]="bf.info"><mba-icon name="user" size="size-4" />
        <span>Posted{{ postedBy() ? ' by ' + postedBy()!.userAccount.firstName + ' ' + postedBy()!.userAccount.lastName : '' }} on {{ brief.createdDate | date: 'd MMM y' }}</span>
      </div>
    </mbt-briefing-drawer>
    }

    @if (addingProduct()) {
      <mb-brand-products-create (close)="addingProduct.set(false)" (productAdded)="onProductAdded($event)" />
    }
    @if (addingCampaign()) {
      <create-campaign (campaignCreated)="onCampaignCreated($event)" (close)="addingCampaign.set(false)" />
    }
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingBasic {
  protected readonly bf = bf;
  protected readonly svc = inject(BriefingService);
  protected readonly images = inject(ImageService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly productService = inject(ProductService);
  private readonly campaignService = inject(CampaignService);
  private readonly accounts = inject(BusinessAccountService);
  private readonly destroyRef = inject(DestroyRef);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('basic', this.svc.brief()!));
  /** Typed as always present, but missing on some older briefs. */
  protected readonly product = computed(() => this.svc.brief()?.product as UgcProductResponseDto | null | undefined);
  protected readonly campaign = computed(() => this.svc.brief()?.campaign as CampaignDto | null | undefined);
  protected readonly products = signal<UgcProductResponseDto[]>([]);
  protected readonly campaigns = signal<CampaignDto[]>([]);
  private readonly users = signal<CampaignManagerDto[]>([]);

  /** The brief has no creator column; the user who set its current status is the closest record. */
  protected readonly postedBy = computed(() => {
    const uid = this.svc.brief()?.currentStatus?.updatedBy;
    return uid ? this.users().find((user) => user.userAccount?.firebaseUid === uid) ?? null : null;
  });

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly addingProduct = signal(false);
  protected readonly addingCampaign = signal(false);
  protected readonly title = signal('');
  protected readonly productId = signal('');
  protected readonly campaignId = signal('');
  protected readonly selectedProduct = computed(() => this.products().find((p) => p.id === this.productId()) ?? null);

  constructor() {
    const brandId = this.currentUser.focussedBrand?.id;
    if (!brandId) {
      return;
    }
    this.accounts.getAccountUsers(brandId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => this.users.set(users ?? []));
  }

  open(): void {
    const brief = this.svc.brief();
    if (!brief) {
      return;
    }
    this.title.set(brief.title ?? '');
    this.productId.set(brief.productId ?? '');
    this.campaignId.set(brief.campaignId ?? '');
    this.loadLists();
    this.editing.set(true);
  }

  private loadLists(): void {
    const brandId = this.currentUser.focussedBrand?.id;
    if (!brandId) {
      return;
    }
    this.productService.getbusinessProducts(brandId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((products) => this.products.set((products as UgcProductResponseDto[]) ?? []));
    this.campaignService.getAllBusinessCampaigns(brandId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((campaigns) => this.campaigns.set(campaigns ?? []));
  }

  protected value(event: Event): string {
    return (event.target as HTMLInputElement | HTMLSelectElement).value;
  }

  protected onProductAdded(product: UgcProductResponseDto): void {
    this.addingProduct.set(false);
    this.products.update((list) => [product, ...list]);
    this.productId.set(product.id ?? '');
  }

  protected onCampaignCreated(campaign: CampaignDto): void {
    this.addingCampaign.set(false);
    this.campaigns.update((list) => [campaign, ...list.filter((c) => c.id !== campaign.id)]);
    this.campaignId.set(campaign.id ?? '');
  }

  protected async save(): Promise<void> {
    if (!this.title().trim()) {
      this.svc.error('Add a brief title');
      return;
    }
    if (!this.productId()) {
      this.svc.error('Choose a product');
      return;
    }
    this.saving.set(true);
    const ok = await this.svc.save(
      [() => this.svc.basicInfo({ title: this.title().trim(), productId: this.productId(), campaignId: this.campaignId() })],
      'Basic Info saved',
    );
    this.saving.set(false);
    if (ok) {
      this.editing.set(false);
    }
  }
}
